import React, { useCallback, useMemo, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import { format, isValid, parse } from 'date-fns';
import { IconBoldCalendar } from 'design-system/icons';
import useColors from 'hooks/useColors';
const DATE_FORMAT = 'yyyy-MM-dd';
const TIME_FORMAT = 'HH:mm';
const INPUT_MARGIN_BOTTOM = 16;
// Space between the modal's close button and the input
const INPUT_MARGIN_TOP = 16;
// Removes the image of the browser's calendar button, which ours is drawn over. The button
// stays, so clicking the icon still opens the calendar and keyboard focus still shows.
const HIDE_BROWSER_CALENDAR_ICON = `
  input[data-emma-date-picker]::-webkit-calendar-picker-indicator {
    background-image: none;
    cursor: pointer;
  }
`;
// Firefox has no such selector and keeps its own icon, so ours is only drawn where the
// browser's can be hidden. The project has no DOM typings, hence the cast.
const browserCss = window.CSS;
const CAN_REPLACE_CALENDAR_ICON = !!browserCss?.supports('selector(::-webkit-calendar-picker-indicator)');
/**
 * Web version of `@react-native-community/datetimepicker`, which has no web
 * support: the browser's own date or time input. It calls `onChange` like
 * the native picker, so `useDateTimePicker` works unchanged.
 */
const DateTimePicker = ({ value, mode = 'date', minimumDate, maximumDate, onChange, themeVariant, }) => {
    const colors = useColors();
    const inputFormat = mode === 'time' ? TIME_FORMAT : DATE_FORMAT;
    // The last date passed to onChange, which is the date Save submits
    const acceptedDate = useRef(value);
    const readInput = useCallback((target) => {
        // The project has no DOM typings, so the input is reached through this cast
        const input = target;
        const date = parse(input.value, inputFormat, value);
        return { input, date: isValid(date) ? date : undefined };
    }, [inputFormat, value]);
    // min and max only limit the browser's calendar, a typed date can still be out of range.
    // Dates in yyyy-MM-dd compare correctly as strings.
    const isBeforeMinimum = useCallback((date) => mode === 'date' &&
        !!minimumDate &&
        format(date, DATE_FORMAT) < format(minimumDate, DATE_FORMAT), [minimumDate, mode]);
    const isAfterMaximum = useCallback((date) => mode === 'date' &&
        !!maximumDate &&
        format(date, DATE_FORMAT) > format(maximumDate, DATE_FORMAT), [maximumDate, mode]);
    const accept = useCallback((date) => {
        acceptedDate.current = date;
        onChange?.({ type: 'set' }, date);
    }, [onChange]);
    // While typing, the browser reports partial dates (a year of 0002 on the way to 2027),
    // so out-of-range or incomplete dates are only corrected on blur
    const handleChange = useCallback((event) => {
        const { date } = readInput(event.target);
        if (date && !isBeforeMinimum(date) && !isAfterMaximum(date)) {
            accept(date);
        }
    }, [accept, isAfterMaximum, isBeforeMinimum, readInput]);
    // Snaps the input to the nearest allowed date, as the native wheel does, so the date on
    // screen is the one Save submits. Pressing Save blurs the input before the press lands.
    const handleBlur = useCallback((event) => {
        const { input, date } = readInput(event.target);
        let shownDate;
        if (!date) {
            shownDate = acceptedDate.current;
        }
        else if (isBeforeMinimum(date) && minimumDate) {
            shownDate = minimumDate;
        }
        else if (isAfterMaximum(date) && maximumDate) {
            shownDate = maximumDate;
        }
        else {
            return;
        }
        input.value = format(shownDate, inputFormat);
        accept(shownDate);
    }, [
        accept,
        inputFormat,
        isAfterMaximum,
        isBeforeMinimum,
        maximumDate,
        minimumDate,
        readInput,
    ]);
    const style = useMemo(() => ({
        width: '100%',
        boxSizing: 'border-box',
        marginTop: INPUT_MARGIN_TOP,
        marginBottom: INPUT_MARGIN_BOTTOM,
        padding: 12,
        borderRadius: 12,
        border: 'none',
        fontFamily: 'Montserrat-Regular',
        fontSize: 16,
        color: colors.text.primary,
        backgroundColor: colors.background.dark,
        colorScheme: themeVariant,
    }), [colors.background.dark, colors.text.primary, themeVariant]);
    const input = (<input data-emma-date-picker={mode === 'date' ? '' : undefined} type={mode} defaultValue={format(value, inputFormat)} min={mode === 'date' && minimumDate
            ? format(minimumDate, DATE_FORMAT)
            : undefined} max={mode === 'date' && maximumDate
            ? format(maximumDate, DATE_FORMAT)
            : undefined} onChange={handleChange} onBlur={handleBlur} style={style}/>);
    if (mode !== 'date' || !CAN_REPLACE_CALENDAR_ICON) {
        return input;
    }
    return (<View>
      <style>{HIDE_BROWSER_CALENDAR_ICON}</style>
      {input}
      <View pointerEvents="none" style={staticStyles.icon}>
        <IconBoldCalendar colorKey="primary"/>
      </View>
    </View>);
};
const staticStyles = StyleSheet.create({
    icon: {
        position: 'absolute',
        top: INPUT_MARGIN_TOP,
        right: 12,
        bottom: INPUT_MARGIN_BOTTOM,
        justifyContent: 'center',
    },
});
export default DateTimePicker;
